import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { isSidePanelShareable, useShareable } from 'features/mfe_shareable'
import { useRightRail } from 'features/right_rail'
import { hideSidebarPlaceholder, isSidebarOpenSelector } from 'features/sidebar_placeholder'
import { useIsUserGuideExpanded, useToggleUserGuide } from 'features/user_guide'

interface SidePanelState {
  isOpen: boolean
  close: () => void
}

/**
 * Whether the shell should render the third column (`#shell-side-panel`), and a stable
 * callback to dismiss whichever source is currently active.
 *
 * The column is omitted when nothing uses it (empty grid track otherwise).
 * Getting Started Guide reserves the column only when expanded (`useIsUserGuideExpanded`).
 */
export function useSidePanel(): SidePanelState {
  const dispatch = useDispatch()
  const { isOpen: isRightRailOpen, close: closeRightRail } = useRightRail()
  const { isOpen: isShareableOpen, close: closeShareable, current: currentShareable } = useShareable()
  const isSidebarPlaceholderOpen = useSelector(isSidebarOpenSelector)
  const isUserGuideExpanded = useIsUserGuideExpanded()
  const [, toggleUserGuide] = useToggleUserGuide()
  const currentShareableName = currentShareable?.shareable
  const isSidePanelShareableOpen = isShareableOpen && isSidePanelShareable(currentShareableName)

  const isOpen = isRightRailOpen
    || isSidePanelShareableOpen
    || isSidebarPlaceholderOpen
    || isUserGuideExpanded

  const close = useCallback(() => {
    if (isRightRailOpen) closeRightRail()
    if (isSidePanelShareableOpen) closeShareable()
    if (isSidebarPlaceholderOpen) dispatch(hideSidebarPlaceholder())
    if (isUserGuideExpanded) toggleUserGuide()
  }, [isRightRailOpen,
    closeRightRail,
    isSidePanelShareableOpen,
    closeShareable,
    isSidebarPlaceholderOpen,
    isUserGuideExpanded,
    toggleUserGuide,
    dispatch])

  return { isOpen, close }
}
